import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { ClickableBanner } from 'component/banner';
import { Pad } from 'component/basics';
import { CTAButton } from 'component/button';
import { ToggleSwitch } from 'component/form/toggle-switch';
import { RichText } from 'component/richtext';
import { Heading, Paragraph } from 'component/text';
import { useVideo } from 'open-modules/zdf/component/videoplayer/videoplayer-state';
import {
    updateVideoVotingSessionData,
    useVideoVotingSessionData,
    VIDEO_VOTING_SESSION_STATES,
} from 'open-modules/zdf/videovoting/videoVotingSession.tsx';
import { exitFullScreen } from 'platform-specific/zdf/exitFullScreen';
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { trackEvent } from 'util/analytics';
import { useDatastore, usePersonaKey } from 'util/datastore';
import { t } from 'util/stdtypes';

import { VideoVotingOverlay } from './videovoting-overlay';

const notLoggedInText = t(
    'video_voting.not_logged_in_text',
    "You have to be logged in to use this feature.\nThe video will play as usual and you won't see any votings.\n**Click here to log in**.",
);
const introText = t(
    'video_voting.intro_text',
    'This video contains audience questions that you can participate in. You can vote anonymously, skip individual questions, or disable this feature. In the ',
);

function VotingOptions({
    votingDisabledLocally,
    toggleVideoVoting,
    anonymityVoting,
    setAnonymity,
}: {
    votingDisabledLocally: boolean;
    toggleVideoVoting: () => void;
    anonymityVoting: boolean;
    setAnonymity: () => void;
}) {
    const s = useIntroScreenStyle();
    return (
        <View style={s.optionsContainer}>
            <ToggleSwitch
                label={t('video_voting.enable_voting', 'Enable voting')}
                onChange={toggleVideoVoting}
                spread
                value={!votingDisabledLocally}
            />
            <View style={{ opacity: !votingDisabledLocally ? 1 : 0 }}>
                <Pad size={10} />
                <ToggleSwitch
                    disabled={votingDisabledLocally}
                    label={t('video_voting.vote_anonymously', 'Vote anonymously')}
                    onChange={setAnonymity}
                    spread
                    value={anonymityVoting}
                />
            </View>
        </View>
    );
}

function LoginBanner() {
    const datastore = useDatastore();
    const { colors } = useTheme();
    return (
        <ClickableBanner
            onPress={() => {
                datastore.gotoInstance({ structureKey: 'login', instanceKey: 'login' });
            }}
            testID='videoVotingLogin'
        >
            <RichText color={colors.colorTextOnTagLight} label={notLoggedInText} />
        </ClickableBanner>
    );
}

function IntroText() {
    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const session = useVideoVotingSessionData();

    const gotoUserSettings = () => {
        exitFullScreen();
        datastore.gotoInstance({ structureKey: 'account', instanceKey: personaKey ?? undefined });
        trackEvent(datastore, 'videovoting-navigate-to-user-settings', {
            from: 'intro-screen',
            videoVotingSessionId: session.id,
        });
    };

    return (
        <Text>
            <Paragraph label={introText} />
            <Pressable onPress={gotoUserSettings} testID='videoVotingSettings'>
                <Paragraph label={t('video_voting.settings', 'settings')} strong />
            </Pressable>
            <Paragraph label={t('video_voting.deactivate_all', ' you can deactivate it for all videos.')} />
        </Text>
    );
}

export function VideoVotingIntroScreen() {
    const s = useIntroScreenStyle();

    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const session = useVideoVotingSessionData();
    const video = useVideo();

    const [anonymityVoting, setAnonymityVoting] = useState(false);
    const [votingDisabledLocally, setVotingDisabledLocally] = useState(false);

    const startSession = () => {
        trackEvent(datastore, 'videovoting-start-video', {
            votingDisabled: votingDisabledLocally.toString(),
            anonymous: anonymityVoting.toString(),
            videoVotingSessionId: session.id,
        });

        if (!votingDisabledLocally && personaKey) {
            updateVideoVotingSessionData(datastore, { state: VIDEO_VOTING_SESSION_STATES.waitForQuestion, personaKey });
            video.play?.();
        } else {
            updateVideoVotingSessionData(datastore, { state: VIDEO_VOTING_SESSION_STATES.skippedVoting });
            video.play?.();
        }
    };

    const setAnonymity = () => {
        const newAnonymityVoting = !anonymityVoting;
        setAnonymityVoting(newAnonymityVoting);
        updateVideoVotingSessionData(datastore, { anonymous: newAnonymityVoting });
        trackEvent(datastore, 'videovoting-change-anonymity', {
            anonymous: newAnonymityVoting.toString(),
            videoVotingSessionId: session.id,
        });
    };

    const toggleVideoVoting = () => {
        const newVotingDisabledLocally = !votingDisabledLocally;
        setVotingDisabledLocally(newVotingDisabledLocally);
        trackEvent(datastore, 'videovoting-change-local-setting', {
            votingDisabledLocally: newVotingDisabledLocally.toString(),
            videoVotingSessionId: session.id,
        });
    };

    useEffect(() => {
        video?.pause?.();
    }, [video]);

    return (
        <VideoVotingOverlay style={s.overlay}>
            <View style={s.backgroundContainer}>
                <Heading label={t('video_voting.title', 'Video Voting')} />
                <Pad size={8} />
                <ScrollView>
                    <IntroText />
                    <Pad size={24} />
                    {personaKey ? (
                        <VotingOptions
                            anonymityVoting={anonymityVoting}
                            setAnonymity={setAnonymity}
                            toggleVideoVoting={toggleVideoVoting}
                            votingDisabledLocally={votingDisabledLocally}
                        />
                    ) : (
                        <LoginBanner />
                    )}
                </ScrollView>
                <Pad size={24} />
                <CTAButton label={t('video_voting.start_video', 'Start Video')} onPress={startSession} size='small' />
            </View>
        </VideoVotingOverlay>
    );
}

const useIntroScreenStyle = createThemedStyles((tokens) => ({
    backgroundContainer: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderRadius: 6,
        padding: 20,
        height: '100%',
    },
    optionsContainer: {
        alignSelf: 'flex-start',
    },
    overlay: {
        width: '65%',
        minWidth: 495,
        maxWidth: 645,
        minHeight: 128,
        maxHeight: '75%',
    },
}));
